/**
 * The response chart's words about time: the line above the frame that says
 * which window is on screen, the x axis labels and the tooltip's time.
 *
 * Dates and clock times take the Estonian shape ("25.9", "14:00") like every
 * CRM date (`crm/ui/format.ts`); only the words come from the catalogue.
 *
 * The window line answers "what am I looking at" before the reader has to
 * decode an axis: "Last 24 h" while the chart follows the newest check at a
 * ladder zoom, otherwise the two ends, with today and yesterday named because
 * that is how an outage is talked about ("yesterday evening").
 */
import { ZOOM_SPANS_MS, formatSpan, type MonitorSeriesPoint } from "../../../../lib/monitoring";
import type { T } from "../words";

export const clock = (ms: number): string =>
  new Date(ms).toLocaleTimeString("et-EE", { hour: "2-digit", minute: "2-digit" });

export const dayMonth = (ms: number): string =>
  new Date(ms).toLocaleDateString("et-EE", { day: "numeric", month: "numeric" });

const sameDay = (a: number, b: number) => new Date(a).toDateString() === new Date(b).toDateString();

const capitalize = (text: string) => text.charAt(0).toLocaleUpperCase() + text.slice(1);

/** "30 min", "24 h", "7 d": a zoom level in the catalogue's units. */
export function spanText(t: T, span: number): string {
  return formatSpan(span, { d: t("monitoring.unit.d"), h: t("monitoring.unit.h"), min: t("monitoring.unit.min") });
}

/**
 * An x axis label. Clock times, except that a tick on a local midnight names
 * the day instead of saying 00:00, so a window across midnight shows where
 * one day turns into the next. Past four days every tick is a day.
 */
export function tickText(ms: number, shape: "time" | "dayTime" | "day"): string {
  if (shape === "day") return dayMonth(ms);
  const d = new Date(ms);
  return d.getHours() === 0 && d.getMinutes() === 0 ? dayMonth(ms) : clock(ms);
}

/** The catalogue's word for today or yesterday, otherwise the date. */
function dayWord(t: T, ms: number, now: number): string {
  if (sameDay(ms, now)) return t("monitoring.chart.window.today");
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (sameDay(ms, yesterday.getTime())) return t("monitoring.chart.window.yesterday");
  return dayMonth(ms);
}

/**
 * The window on screen, [from, to): "Last 24 h" when it follows the newest
 * check at a ladder zoom; "Today 08:00 - 14:30" within one day; "Yesterday
 * 20:00 - today 02:00" or "22.9 14:00 - 25.9 14:00" across days.
 *
 * "Last" agrees with the span's number in Estonian ("Viimane 1 h", "Viimased
 * 24 h"), so the catalogue entry is a plural keyed on it. Every ladder span
 * reads in one unit with its number first (`formatSpan`), so that number is
 * the count.
 */
export function windowText(t: T, from: number, to: number, now: number, pinned: boolean): string {
  const span = to - from;
  if (pinned && ZOOM_SPANS_MS.includes(span)) {
    const text = spanText(t, span);
    const n = Number.parseInt(text, 10);
    return t("monitoring.chart.window.last", { span: text, n });
  }
  const start = `${dayWord(t, from, now)} ${clock(from)}`;
  const end = sameDay(from, to) ? clock(to) : `${dayWord(t, to, now)} ${clock(to)}`;
  return capitalize(`${start} - ${end}`);
}

/**
 * The tooltip's time: a check's own time, or a bucket's range ("14:00-14:15"),
 * with the date in front when the window is long enough that the axis no
 * longer says which day it is.
 */
export function pointTime(p: MonitorSeriesPoint, step: number, withDay: boolean): string {
  const day = withDay ? `${dayMonth(p.t)} ` : "";
  if (step === 0) return `${day}${clock(p.t)}`;
  return `${day}${clock(p.t)}-${clock(p.t + step * 1000)}`;
}
